/**
 * API client for Clip Monitor Server
 */

import { useAuthStore } from '../stores/authStore';

const CLIP_MONITOR_BASE_URL = process.env.REACT_APP_CLIP_MONITOR_URL || 'http://localhost:20042';
const APP_AUTH_ERROR_HEADER = 'x-app-auth-error';

/** True when the app is configured to use a remote clip server (e.g. ngrok) rather than localhost. */
export const CLIP_MONITOR_IS_REMOTE =
  typeof CLIP_MONITOR_BASE_URL === 'string' && !CLIP_MONITOR_BASE_URL.startsWith('http://localhost:');

let isHandlingClipSessionExpiry = false;

/**
 * Authenticated fetch wrapper for clip server requests.
 * Attaches Bearer token from localStorage and handles explicit app-session expiry responses.
 */
async function clipFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401 && response.headers.get(APP_AUTH_ERROR_HEADER) === 'session') {
    if (!isHandlingClipSessionExpiry) {
      isHandlingClipSessionExpiry = true;
      useAuthStore.getState().handleSessionExpired();
      setTimeout(() => { isHandlingClipSessionExpiry = false; }, 2000);
    }
  }

  return response;
}

export interface ClipFile {
  clipAddress: string;
  filename: string;
  recordCount: number;
  lastUpdate: string;
  fileSize: number;
  annotationCount?: number;
  displayLabel?: string;
  dateRange?: string;
}

export interface ClipDataPoint {
  timestamp: string;
  payloadTime: string;
  clipAddress: string;
  relayAddress?: string;
  relayAndroidId?: string;
  rssi?: number;
  payload: number[];
  parsed: {
    firmwareRevision: number;
    statusCode: number;
    wetnessLevel: number;
    diaperModel: number;
    diaperCounter: number;
    adcValues: number[];
    batteryPercent: number;
  };
}

export interface ServerConfig {
  proxyEnabled: boolean;
  proxyUrl: string | null;
  dataDir: string;
}

export interface LiquidAddition {
  timestamp: string;
  volume: number;
  duration: number;
  location: string;
  diaperCounter?: number; // Added to group by diaper
}

export interface Annotation {
  id: string;
  createdAt: string;
  diaperCounter: number | null;
  tags: string[];
  text: string;
  timeRange: { start: string; end: string } | null;
  measurementTimestamp: string | null;
}

export interface FirmwareModelConstants {
  name: string;
  poolingThreshold: number;
  poolingEffectivelyDisabled: boolean;
  sensorHighValues10bit: number[] | null;
  sensorHighValues8bit: number[] | null;
  lineLengthMm: number | null;
  lineSeparationMm: number | null;
}

export interface FirmwareConstants {
  firmwareVersion: number;
  global: {
    poolingSubTimeMinutes: number;
    wickingPeakThresholds: number[];
    wickingRateThreshold: number;
    GE: number;
    OE: number;
    sensorLowValueDefault: number;
    sensorLowValueFormula: string;
    adcMax: number;
    poolingCondition: string;
  };
  models: Record<string, FirmwareModelConstants>;
}

export interface BackendEmiction {
  time: string;
  delta_ml: number;
  cumulative_ml: number;
  leak: boolean;
}

export interface BackendAlerts {
  wet_time?: string | null;
  wet_alert_time?: string | null;
  leak_time?: string | null;
  leak_alert_time?: string | null;
  pool_time?: string | null;
  pool_alert_time?: string | null;
  remove_pool_time?: string | null;
}

export interface BackendDiaperModel {
  model_code: number;
  name: string;
  capacity_ml: number;
  coefficients: number[];
  leak_threshold?: { adcValues: number[]; wetnessLevel: number };
}

export interface BackendCriterion {
  criterion_id: number;
  name: string;
  config: Record<string, any>;
}

export interface WetnessSample {
  time: string;
  wetnessLevel: number;
  diaperCounter: number;
}

export interface BackendDiaper {
  diaper_id: number;
  model_code: number;
  start_time: string;
  end_time: string;
  close_time: string;
  closing_volume: number;
  closing_percent: number;
  closing_level: number;
  emictions: BackendEmiction[];
  alerts: BackendAlerts;
  diaper_model?: BackendDiaperModel;
  criterion?: BackendCriterion;
}

export interface BackendMeta {
  clip_address: string;
  clip_name: string;
  site_name: string;
  diapers: BackendDiaper[];
}

/**
 * One session from diaper-accuracy1 pipeline results.json → usage.session_samples
 * (plus clip_address / clip_stem from clip server).
 */
export interface AnalysisSession {
  clip: string;
  clip_stem?: string;
  /** MAC address for UI / diaper-inspector ?clip= */
  clip_address: string;
  counter: number;
  model: number;
  fw?: number;
  start_ts: string;
  end_ts: string;
  site_name?: string;
  flatline_like?: boolean;
  mostly_dry?: boolean;
  quickly_high_wetness?: boolean;
  left_truncated?: boolean;
  right_truncated?: boolean;
  session_complete?: boolean;
  baseline_trustworthy?: boolean;
  backend_matched?: boolean;
  is_day_start?: boolean;
  duration_hours?: number;
  duration_sec?: number;
  wetness_per_hour?: number;
  max_wetness?: number;
  baseline_wetness?: number;
  /** Pipeline may add many more metrics; UI treats rows as Record<string, unknown>. */
  phase_metrics?: unknown;
  gap_buckets?: unknown;
  [key: string]: unknown;
}

export interface AnalysisSessionsResult {
  ok: boolean;
  bucket?: string;
  resultsPath?: string | null;
  /** Resolved diaper-accuracy1/results directory (404 diagnostics) */
  analysisBaseDir?: string | null;
  sessions: AnalysisSession[];
  error?: string;
  message?: string;
}

export interface SourceFolder {
  id: string;
  label: string;
  path: string;
  available: boolean;
}

/**
 * List available source folders
 */
export async function listSources(): Promise<{ sources: SourceFolder[]; activeSource: string }> {
  const response = await clipFetch(`${CLIP_MONITOR_BASE_URL}/api/sources`);
  if (!response.ok) {
    throw new Error('Failed to list sources');
  }
  return response.json();
}

/**
 * Select a source folder
 */
export async function selectSource(sourceId: string): Promise<void> {
  const response = await clipFetch(`${CLIP_MONITOR_BASE_URL}/api/sources/select`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceId }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to select source');
  }
}

/**
 * Get server configuration
 */
export async function getServerConfig(): Promise<ServerConfig> {
  const response = await clipFetch(`${CLIP_MONITOR_BASE_URL}/api/config`);
  if (!response.ok) {
    throw new Error('Failed to get server configuration');
  }
  return response.json();
}

/**
 * Update server configuration
 */
export async function updateServerConfig(config: Partial<ServerConfig>): Promise<void> {
  const response = await clipFetch(`${CLIP_MONITOR_BASE_URL}/api/config`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(config),
  });
  
  if (!response.ok) {
    throw new Error('Failed to update server configuration');
  }
}

/**
 * List available buckets
 */
export async function listBuckets(): Promise<{ buckets: string[]; activeBucket: string | null }> {
  const response = await clipFetch(`${CLIP_MONITOR_BASE_URL}/api/buckets`);
  if (!response.ok) {
    throw new Error('Failed to list buckets');
  }
  return response.json();
}

/**
 * Select a bucket
 */
export async function selectBucket(bucket: string | null): Promise<void> {
  const response = await clipFetch(`${CLIP_MONITOR_BASE_URL}/api/buckets/select`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bucket }),
  });
  if (!response.ok) {
    throw new Error('Failed to select bucket');
  }
}

/**
 * List all available clip files
 */
export async function listClipFiles(): Promise<ClipFile[]> {
  const response = await clipFetch(`${CLIP_MONITOR_BASE_URL}/api/clips`);
  if (!response.ok) {
    throw new Error('Failed to list clip files');
  }
  const data = await response.json();
  return data.clips || [];
}

/**
 * Get data for a specific clip
 */
export async function getClipData(
  clipAddress: string,
  limit?: number
): Promise<ClipDataPoint[]> {
  const url = new URL(`${CLIP_MONITOR_BASE_URL}/api/clips/${encodeURIComponent(clipAddress)}`);
  if (limit) {
    url.searchParams.append('limit', limit.toString());
  }

  const response = await clipFetch(url.toString());
  if (!response.ok) {
    throw new Error(`Failed to get clip data for ${clipAddress}`);
  }
  const data = await response.json();
  return data.records || [];
}

/**
 * Delete data for a specific clip
 */
export async function deleteClipData(clipAddress: string): Promise<void> {
  const response = await clipFetch(
    `${CLIP_MONITOR_BASE_URL}/api/clips/${encodeURIComponent(clipAddress)}`,
    {
      method: 'DELETE',
    }
  );
  
  if (!response.ok) {
    throw new Error(`Failed to delete clip data for ${clipAddress}`);
  }
}

/**
 * Add a manual liquid addition record
 */
export async function addLiquidAddition(
  clipAddress: string,
  volume: number,
  duration: number,
  location: string,
  diaperCounter?: number
): Promise<LiquidAddition> {
  const response = await clipFetch(
    `${CLIP_MONITOR_BASE_URL}/api/clips/${encodeURIComponent(clipAddress)}/liquid-additions`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ volume, duration, location, diaperCounter }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to add liquid addition for ${clipAddress}`);
  }

  const data = await response.json();
  return data.addition;
}

/**
 * Get all annotations for a clip
 */
export async function getClipMeta(clipAddress: string): Promise<BackendMeta | null> {
  try {
    const response = await clipFetch(
      `${CLIP_MONITOR_BASE_URL}/api/clips/${encodeURIComponent(clipAddress)}/meta`
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data.meta || null;
  } catch {
    return null;
  }
}

/**
 * Get all backend meta data for all clips in the current bucket
 */
export async function getAllClipMeta(): Promise<Record<string, BackendMeta>> {
  try {
    const response = await clipFetch(`${CLIP_MONITOR_BASE_URL}/api/clips/meta-all`);
    if (!response.ok) return {};
    const data = await response.json();
    return data.metas || {};
  } catch {
    return {};
  }
}

/**
 * Pipeline analysis sessions (flatline_like, mostly_dry, etc.) from diaper-accuracy1/results/{bucket}/results.json
 */
export async function getAnalysisSessions(bucket: string): Promise<AnalysisSessionsResult> {
  if (!bucket?.trim()) {
    return { ok: false, error: 'bucket_required', message: 'Period bucket is required', sessions: [] };
  }
  try {
    const params = new URLSearchParams({ bucket: bucket.trim() });
    const response = await clipFetch(
      `${CLIP_MONITOR_BASE_URL}/api/analysis/sessions?${params.toString()}`
    );
    const data = await response.json().catch(() => ({}));
    if (response.status === 404) {
      return {
        ok: false,
        error: data.error || 'results_not_found',
        message: data.message || 'No results.json for this period',
        resultsPath: data.resultsPath,
        analysisBaseDir: data.analysisBaseDir,
        sessions: [],
      };
    }
    if (!response.ok) {
      return {
        ok: false,
        error: data.error || 'request_failed',
        message: data.message || `HTTP ${response.status}`,
        sessions: [],
      };
    }
    return {
      ok: true,
      bucket: data.bucket,
      resultsPath: data.resultsPath,
      analysisBaseDir: data.analysisBaseDir ?? null,
      sessions: (data.sessions || []) as AnalysisSession[],
    };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, error: 'network', message: msg, sessions: [] };
  }
}

/**
 * Get sampled wetness data for specific clips (fast seek-based sampling on server)
 */
export async function getWetnessSamplesForClips(
  clipAddresses: string[],
  samplesPerClip: number = 300
): Promise<Record<string, WetnessSample[]>> {
  if (clipAddresses.length === 0) return {};
  try {
    const clipsParam = clipAddresses.map(encodeURIComponent).join(',');
    const response = await clipFetch(
      `${CLIP_MONITOR_BASE_URL}/api/clips/wetness-samples?samples=${samplesPerClip}&clips=${clipsParam}`
    );
    if (!response.ok) return {};
    const data = await response.json();
    return data.samples || {};
  } catch {
    return {};
  }
}

export async function getAnnotations(clipAddress: string): Promise<Annotation[]> {
  const response = await clipFetch(
    `${CLIP_MONITOR_BASE_URL}/api/clips/${encodeURIComponent(clipAddress)}/annotations`
  );

  if (!response.ok) {
    throw new Error(`Failed to get annotations for ${clipAddress}`);
  }

  const data = await response.json();
  return data.annotations || [];
}

/**
 * Add an annotation
 */
export async function addAnnotation(
  clipAddress: string,
  data: {
    tags?: string[];
    text: string;
    diaperCounter?: number | null;
    timeRange?: { start: string; end: string } | null;
    measurementTimestamp?: string | null;
  }
): Promise<Annotation> {
  const response = await clipFetch(
    `${CLIP_MONITOR_BASE_URL}/api/clips/${encodeURIComponent(clipAddress)}/annotations`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to add annotation for ${clipAddress}`);
  }

  const result = await response.json();
  return result.annotation;
}

/**
 * Update an annotation
 */
export async function updateAnnotation(
  clipAddress: string,
  id: string,
  data: Partial<Annotation>
): Promise<Annotation> {
  const response = await clipFetch(
    `${CLIP_MONITOR_BASE_URL}/api/clips/${encodeURIComponent(clipAddress)}/annotations/${encodeURIComponent(id)}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update annotation for ${clipAddress}`);
  }

  const result = await response.json();
  return result.annotation;
}

/**
 * Delete an annotation
 */
export async function deleteAnnotation(
  clipAddress: string,
  id: string
): Promise<void> {
  const response = await clipFetch(
    `${CLIP_MONITOR_BASE_URL}/api/clips/${encodeURIComponent(clipAddress)}/annotations/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to delete annotation ${id}`);
  }
}

/**
 * Get all liquid additions for a clip
 */
export async function getLiquidAdditions(clipAddress: string): Promise<LiquidAddition[]> {
  const response = await clipFetch(
    `${CLIP_MONITOR_BASE_URL}/api/clips/${encodeURIComponent(clipAddress)}/liquid-additions`
  );

  if (!response.ok) {
    throw new Error(`Failed to get liquid additions for ${clipAddress}`);
  }

  const data = await response.json();
  return data.additions || [];
}

/**
 * Get firmware constants (served from local server data, not bundled)
 * Returns null if not available (production, server down, file missing)
 */
export async function getFirmwareConstants(): Promise<FirmwareConstants | null> {
  try {
    const response = await clipFetch(`${CLIP_MONITOR_BASE_URL}/api/firmware-constants`);
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Check if server is running
 */
export async function checkServerHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${CLIP_MONITOR_BASE_URL}/health`);
    return response.ok;
  } catch (error) {
    return false;
  }
}
